import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

const PasswordRequirements = (props) => {
  const { t } = useTranslation();
  const { formSubmitted, password, errorText, showPasswordDiffItem } = props;
  const [liveMessage, setLiveMessage] = React.useState("");
  const previousStatusRef = React.useRef({});

  const hasSpecialCharacter = (value) => {
    const specialCharacterRegEx = new RegExp(
      "[!\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~]"
    );
    return specialCharacterRegEx.test(value);
  };
  const isPassedPasswordRequirements = (key) => {
    let classNameText = "";
    switch (key) {
      case "nineCharacters":
        if (formSubmitted && password.length < 9) {
          classNameText = "failed";
        } else if (password.length >= 9) {
          classNameText = "passed";
        }
        break;
      case "capitalLetter":
        if (formSubmitted && !password.match("[A-Z]")) {
          classNameText = "failed";
        } else if (password.match("[A-Z]")) {
          classNameText = "passed";
        }
        break;
      case "oneNumber":
        if (formSubmitted && !password.match("[0-9]")) {
          classNameText = "failed";
        } else if (password.match("[0-9]")) {
          classNameText = "passed";
        }
        break;
      case "smallLetter":
        if (formSubmitted && !password.match("[a-z]")) {
          classNameText = "failed";
        } else if (password.match("[a-z]")) {
          classNameText = "passed";
        }
        break;
      case "charactersAllowed":
        if (formSubmitted && (password.length > 64 || password.length === 0)) {
          classNameText = "failed";
        } else if (password.length <= 64 && password.length !== 0) {
          classNameText = "passed";
        }
        break;
      case "specialCharacter":
        if (formSubmitted && !hasSpecialCharacter(password)) {
          classNameText = "failed";
        } else if (hasSpecialCharacter(password)) {
          classNameText = "passed";
        }
        break;
      case "compareWithOldPassword":
        if (
          password.length > 0 &&
          errorText !== "pages.passwordReset.mustBeNewPassword"
        ) {
          classNameText = "passed";
        }
        break;
      default:
        classNameText = "";
        break;
    }
    return classNameText;
  };

  const getRequirementStatusText = (status) => {
    if (status === "passed") {
      return "Passed";
    }
    return "Not passed";
  };

  const requirements = React.useMemo(
    () => [
      { key: "nineCharacters", label: t("pages.registration.nineCharacters") },
      { key: "capitalLetter", label: t("pages.registration.capitalLetter") },
      ...(showPasswordDiffItem
        ? [
            {
              key: "compareWithOldPassword",
              label: t("pages.registration.differentFromCurrentPassword")
            }
          ]
        : []),
      { key: "oneNumber", label: t("pages.registration.oneNumber") },
      { key: "smallLetter", label: t("pages.registration.oneLowerLetter") },
      { key: "specialCharacter", label: t("pages.registration.specialCharacter") },
      { key: "charactersAllowed", label: t("pages.registration.charactersAllowed") }
    ],
    [showPasswordDiffItem, t]
  );

  React.useEffect(() => {
    const currentStatus = {};
    requirements.forEach((item) => {
      currentStatus[item.key] = isPassedPasswordRequirements(item.key);
    });

    const isFirstAnnouncement = Object.keys(previousStatusRef.current).length === 0;

    if (isFirstAnnouncement) {
      const fullSummary = requirements
        .map(
          (item) =>
            `${item.label}. ${getRequirementStatusText(currentStatus[item.key])}`
        )
        .join(" ");
      setLiveMessage(fullSummary);
      previousStatusRef.current = currentStatus;
      return;
    }

    const changedItems = requirements.filter(
      (item) => previousStatusRef.current[item.key] !== currentStatus[item.key]
    );

    if (changedItems.length > 0) {
      const changedSummary = changedItems
        .map(
          (item) => `${item.label}. ${getRequirementStatusText(currentStatus[item.key])}`
        )
        .join(" ");
      setLiveMessage(changedSummary);
    }

    previousStatusRef.current = currentStatus;
  }, [password, formSubmitted, errorText, requirements]);

  const renderRequirement = (key, label) => {
    const status = isPassedPasswordRequirements(key);
    const statusText = getRequirementStatusText(status);

    return (
      <li className={status} aria-label={`${label}. ${statusText}`}>
        {label}
        <span className="visually-hidden">. {statusText}</span>
      </li>
    );
  };

  return (
    <>
      <span
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="visually-hidden"
      >
        {liveMessage}
      </span>
      <ul className="password-terms list-unstyled" id="password-requirements">
        {renderRequirement(
          "nineCharacters",
          t("pages.registration.nineCharacters")
        )}
        {renderRequirement("capitalLetter", t("pages.registration.capitalLetter"))}
        {showPasswordDiffItem && (
          renderRequirement(
            "compareWithOldPassword",
            t("pages.registration.differentFromCurrentPassword")
          )
        )}
        {renderRequirement("oneNumber", t("pages.registration.oneNumber"))}
        {renderRequirement("smallLetter", t("pages.registration.oneLowerLetter"))}
        {renderRequirement(
          "specialCharacter",
          t("pages.registration.specialCharacter")
        )}
        {renderRequirement(
          "charactersAllowed",
          t("pages.registration.charactersAllowed")
        )}
      </ul>
    </>
  );
};

PasswordRequirements.propTypes = {
  password: PropTypes.string.isRequired,
  formSubmitted: PropTypes.bool.isRequired,
  showPasswordDiffItem: PropTypes.bool,
  errorText: PropTypes.string
};

PasswordRequirements.defaultProps = {
  showPasswordDiffItem: false,
  errorText: ""
};

export default PasswordRequirements;
